import { getTenantMember, TenantMember, TenantMemberEmbeds } from '@kudos/http-client'
import { useQuery } from '@tanstack/react-query'
import { isAxiosError } from 'axios'

/**
 * Fetches TenantMember entity
 * @param tenantMemberId string id for the call
 * @param embeds embeds to request on the member
 * @param enabled gates the query
 * @param skip404Interceptor when true, a 404 rejects locally instead of routing to the global
 *   NotFound page (callers that render a deleted member inline should pass true)
 * @returns Tuple of the TenantMember, its fetched status, and whether it 404'd (member not found)
 */
const useTenantMember = (
  tenantMemberId: string,
  embeds: TenantMemberEmbeds[] = ['avatar', 'bannerUrls', 'publicStaffProfile'],
  enabled = true,
  skip404Interceptor = false,
): [TenantMember | null, boolean, boolean] => {
  const {
    data: tenantMember,
    isFetched,
    isError,
    error,
  } = useQuery<TenantMember>({
    queryKey: ['useTenantMember', tenantMemberId, ...embeds],
    queryFn: () => getTenantMember(tenantMemberId, { embeds }, skip404Interceptor),
    staleTime: 30 * 1000,
    gcTime: 60 * 1000,
    enabled,
  })

  const isNotFound = isAxiosError(error) && error.response?.status === 404

  if (isError) {
    return [null, true, isNotFound]
  }

  return [tenantMember || null, isFetched, isNotFound]
}

export default useTenantMember
